import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ActionModal } from "@cntrl/ui";
import { useActiveBand } from "../../app/activeBand.js";
import { useMe } from "../../app/hooks/useSession.js";
import { useNotificationBandPrompt } from "../../app/notificationBand.js";
import { switchBand } from "../../app/switchBand.js";

/**
 * The "this is for another band" question, mounted ONCE at the router root — a notification can be opened from
 * any shell's bell, the mobile notifications page, or a push tap before any shell has rendered (see
 * `notificationBand.ts`). Continuing runs the audited `switchBand` scorch, then opens the notification's route in
 * the new band. Cancelling leaves the person where they were.
 */
export function NotificationBandPromptHost() {
  const pending = useNotificationBandPrompt((s) => s.pending);
  const clear = useNotificationBandPrompt((s) => s.clear);
  const me = useMe({ enabled: pending !== null });
  const setActiveBand = useActiveBand((s) => s.setActiveBand);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  if (!pending) return null;

  const bandName = (id: string | undefined) =>
    me.data?.seats?.find((s) => s.kind === "band" && s.bandId === id)?.displayName ?? undefined;
  const target = bandName(pending.bandId) ?? "another band";

  async function proceed() {
    if (!pending) return;
    const { route, bandId } = pending;
    setSwitching(true);
    clear();
    try {
      await switchBand(bandId, { queryClient, realtime: null, navigate: (id) => setActiveBand(id) });
      await navigate({ to: route });
    } finally {
      setSwitching(false);
    }
  }

  return (
    <ActionModal
      title={`Switch to ${target}`}
      confirmLabel="Switch"
      onConfirm={() => void proceed()}
      onCancel={clear}
      loading={switching}
    >
      <p>Would you like to switch to {target} and open this notification?</p>
    </ActionModal>
  );
}
